import { useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowsHorizontal, Calendar, ChartRelationship, Login, Logout, Notebook, Purchase,
  UserAvatar, View, Wikis,
  type CarbonIconType,
} from '@carbon/icons-react';
import {
  Content,
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  HeaderPanel,
  SideNav,
  SideNavItems,
  SideNavLink,
  SkipToContent,
  Tag,
  Theme,
} from '@carbon/react';
import { get, type Me } from '@/lib/api';
import { signOut, useSession } from '@/lib/auth-client';

/**
 * The shell.
 *
 * Six destinations, each with a glyph. The icons are not decoration: the side navigation
 * on a narrow screen is a column of six similar phrases, and "Directory" and "Competitors"
 * are two words a reader has to actually read to tell apart. A shape in front of each one
 * is the difference between recognising a destination and parsing it.
 *
 * The account used to be a single sign-out button whose only description lived in an
 * `aria-label`, so a reader could see their plan and their watchlist limit if they used a
 * screen reader, and not otherwise. It is a panel now, which is the Carbon pattern for
 * exactly this, and it says the same things out loud to everybody.
 */

const NAV: { to: string; label: string; icon: CarbonIconType }[] = [
  { to: '/', label: 'This week', icon: Calendar },
  { to: '/competitors', label: 'Competitors', icon: View },
  { to: '/compare', label: 'Compare', icon: ArrowsHorizontal },
  { to: '/directory', label: 'Directory', icon: Wikis },
  { to: '/battlecards', label: 'Battlecards', icon: Notebook },
  { to: '/you', label: 'You', icon: ChartRelationship },
  { to: '/billing', label: 'Plans', icon: Purchase },
];

export function useMe() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/v1/me'),
    enabled: Boolean(session?.user),
  });
}

export function Shell() {
  const { data: session, isPending } = useSession();
  const { data: me } = useMe();
  const [accountOpen, setAccountOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const account = me?.account.email ?? session?.user?.email ?? 'Account';

  const isActive = (to: string) => to === '/' ? path === '/' : path.startsWith(to);

  return (
    <div className="min-h-dvh">
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <Theme theme="g100">
            <Header aria-label="moves.watch" className="print:hidden">
              <SkipToContent />
              {session?.user ? (
                <HeaderMenuButton
                  aria-label={isSideNavExpanded ? 'Close navigation' : 'Open navigation'}
                  isActive={isSideNavExpanded}
                  onClick={onClickSideNavExpand}
                />
              ) : null}
              <HeaderName as={Link} to="/" prefix="">moves.watch</HeaderName>
              {session?.user ? (
                <HeaderNavigation aria-label="Primary navigation">
                  {NAV.map((item) => (
                    <HeaderMenuItem
                      key={item.to}
                      as={Link}
                      to={item.to}
                      isActive={isActive(item.to)}
                      aria-current={isActive(item.to) ? 'page' : undefined}
                    >
                      {item.label}
                    </HeaderMenuItem>
                  ))}
                </HeaderNavigation>
              ) : null}
              <HeaderGlobalBar>
                {session?.user ? (
                  <HeaderGlobalAction
                    aria-label="Account"
                    tooltipAlignment="end"
                    isActive={accountOpen}
                    onClick={() => setAccountOpen((open) => !open)}
                  >
                    <UserAvatar size={20} />
                  </HeaderGlobalAction>
                ) : isPending ? null : (
                  <HeaderGlobalAction
                    aria-label="Sign in"
                    tooltipAlignment="end"
                    onClick={() => window.location.assign('/sign-in')}
                  >
                    <Login size={20} />
                  </HeaderGlobalAction>
                )}
              </HeaderGlobalBar>
              {session?.user ? (
                <HeaderPanel expanded={accountOpen} aria-label="Account">
                  <div className="p-4 text-sm">
                    <p className="truncate">{account}</p>
                    <p className="mt-1 text-xs opacity-70">
                      {me
                        ? `${planName(me.account.plan)} · watching ${me.watching}${me.watchlist_limit ? ` of ${me.watchlist_limit}` : ''}`
                        : 'Loading…'}
                    </p>
                    <button
                      type="button"
                      className="mt-4 flex w-full items-center justify-between gap-4 border-t border-white/20 pt-3 text-left text-sm"
                      onClick={() => void signOut().then(() => window.location.assign('/sign-in'))}
                    >
                      Sign out
                      <Logout size={16} />
                    </button>
                  </div>
                </HeaderPanel>
              ) : null}
            </Header>
            {session?.user ? (
              <SideNav
                aria-label="Primary navigation"
                expanded={isSideNavExpanded}
                isPersistent={false}
                onSideNavBlur={onClickSideNavExpand}
                className="print:hidden"
              >
                <SideNavItems>
                  {NAV.map((item) => (
                    <SideNavLink
                      key={item.to}
                      as={Link}
                      to={item.to}
                      renderIcon={item.icon}
                      isActive={isActive(item.to)}
                      onClick={isSideNavExpanded ? onClickSideNavExpand : undefined}
                    >
                      {item.label}
                    </SideNavLink>
                  ))}
                </SideNavItems>
              </SideNav>
            ) : null}
          </Theme>
        )}
      />
      <Theme theme="white">
        <Content id="main-content" className="app-main p-0">
          {session?.user ? (
            <div className="app-briefing-bar print:hidden">
              <p>Competitor pricing, read weekly and kept on the record</p>
              <span className="app-briefing-facts">
                {me ? (
                  <Tag type={me.account.plan === 'free' ? 'gray' : 'blue'} size="sm">
                    {planName(me.account.plan)}
                  </Tag>
                ) : null}
                <span className="app-briefing-status">
                  <i aria-hidden />
                  {me ? `${me.watching} under watch` : 'Loading…'}
                </span>
              </span>
            </div>
          ) : null}
          <div className="app-workspace">
            <Outlet />
          </div>
        </Content>
      </Theme>
    </div>
  );
}

export function planName(plan: string): string {
  return plan === 'team' || plan === 'scale' ? 'Scale' : plan === 'pro' ? 'Pro' : 'Free';
}
